/**
 * Discriminated union covering the three states a color cell can be in:
 *
 *  - `colored`: the colorReference was found in the resolved map; show
 *    `name` + `labDipNumber` (the lab-dip number may itself be null on
 *    legacy rows, hence the optional field).
 *  - `uncolored`: `colorReference` is null on the roll itself — explicit
 *    "Uncolored / Pending" placeholder, no API call needed.
 *  - `orphan`: `colorReference` is set but the batch response did not
 *    return a record (deleted LabDipRequest, broken link). Display the
 *    short reference id as a diagnostic so ops can fix the upstream link.
 */
export type ColorDisplay =
  | { kind: 'colored'; name: string; labDipNumber: string | null; swatch: ColorSwatch }
  | { kind: 'uncolored' }
  | { kind: 'orphan'; diagnostic: string };

/** Public shape for resolved color records — matches `ColorDto` on the wire. */
export interface ColorRecord {
  id: string;
  name: string;
  labDipNumber: string | null;
}

/**
 * Visual swatch for a color chip — soft tinted background + contrast-safe
 * text. The Item.API color document does NOT carry a hex value, so swatches
 * are derived from the color name via a curated rule list (see
 * `resolveColorSwatch`). Backgrounds are deliberately desaturated (~85-90%
 * lightness for light variants) so the chip reads as a hint, not a paint
 * sample, and never competes with the surrounding financial data.
 */
export interface ColorSwatch {
  bg: string;
  text: string;
}

/** Brand fallback (Sierra Teal soft) for any color name that doesn't match
 *  a known rule. Keeps unknowns on-brand instead of defaulting to neutral
 *  gray, which would silently swallow new color names. */
const FALLBACK_SWATCH: ColorSwatch = { bg: '#dceeec', text: '#2d514e' };

/**
 * Curated name → swatch rules in priority order. Specific compound names
 * sit in the first tier so "Heather Columbia Blue" resolves to the powder
 * blue family rather than the generic gray family attached to the
 * "heather" qualifier. Tier 2 is base color words for everything else.
 */
const SWATCH_RULES: readonly (readonly [RegExp, ColorSwatch])[] = [
  // ── Tier 1: specific compound names ──────────────────────────────────
  // Whites & creams
  [/\b(natural\s*white|cream|ivory|ecru|bone)\b/i, { bg: '#fbf5e6', text: '#5c4a1f' }],
  [/\b(optic\s*white|eco\s*white|off\s*white|snow)\b/i, { bg: '#f4f4f2', text: '#3f3f3f' }],
  // Blacks & near-blacks
  [/\b(obscure(\s*night)?|jet\s*black|midnight\s*black|onyx)\b/i, { bg: '#272a30', text: '#f1f1f1' }],
  // Specific blues
  [/\b(navy|deep\s*blue|midnight\s*blue|marine|nautical)\b/i, { bg: '#c5cfdf', text: '#1b2c4f' }],
  [/\b(columbia\s*blue|powder\s*blue|baby\s*blue|sky\s*blue|sky)\b/i, { bg: '#d8e8f2', text: '#234d71' }],
  [/\b(royal|cobalt|sapphire)\b/i, { bg: '#ccd8eb', text: '#1e3673' }],
  // Specific greens
  [/\b(forest|hunter|emerald|kelly\s*green)\b/i, { bg: '#c5dac8', text: '#1f4029' }],
  [/\b(mint|seafoam|jade)\b/i, { bg: '#d6ecdc', text: '#1f4f37' }],
  [/\b(dusted\s*olive|olive|sage|moss)\b/i, { bg: '#dedfb7', text: '#4a4b14' }],
  // Specific reds & pinks
  [/\b(crimson|burgundy|wine|maroon)\b/i, { bg: '#e5c2c7', text: '#651a1f' }],
  [/\b(hot\s*pink|fuchsia|magenta)\b/i, { bg: '#f8d2dd', text: '#822050' }],
  [/\b(rose|blush|coral|salmon)\b/i, { bg: '#fae0e4', text: '#7a2c46' }],
  // Specific browns & tans
  [/\b(chocolate|coffee|espresso)\b/i, { bg: '#d6c0ae', text: '#4d2e1a' }],
  [/\b(tan|beige|sand|khaki|stone)\b/i, { bg: '#eadfc8', text: '#5c4a2a' }],
  [/\b(taupe|mushroom|fawn)\b/i, { bg: '#dad2c5', text: '#4d4435' }],
  // Specific oranges & yellows
  [/\b(rust|terracotta|burnt\s*orange)\b/i, { bg: '#efd0b5', text: '#74390c' }],
  [/\b(mustard|gold|saffron)\b/i, { bg: '#ede0a8', text: '#5c4a0f' }],
  // Grays
  [/\b(charcoal|graphite|slate)\b/i, { bg: '#c9cdd2', text: '#2d3338' }],

  // ── Tier 2: base color words (catch-alls) ────────────────────────────
  [/\bwhite\b/i, { bg: '#f8f8f8', text: '#3f3f3f' }],
  [/\b(black|noir|carbon)\b/i, { bg: '#2a2d33', text: '#f5f5f5' }],
  [/\b(red|cherry)\b/i, { bg: '#f4d2d0', text: '#7a2a24' }],
  [/\bpink\b/i, { bg: '#fbe3e8', text: '#7a2c46' }],
  [/\b(orange|peach|amber|apricot)\b/i, { bg: '#fae0c5', text: '#7a4012' }],
  [/\b(yellow|lemon|sunshine)\b/i, { bg: '#f5edb7', text: '#5e5210' }],
  [/\b(green|lime)\b/i, { bg: '#d4e4d0', text: '#2a4f26' }],
  [/\b(teal|aqua|cyan)\b/i, { bg: '#c8e2df', text: '#194e48' }],
  [/\b(blue|denim|indigo|cornflower)\b/i, { bg: '#cfddeb', text: '#234060' }],
  [/\b(purple|violet|lavender|lilac|plum|aubergine|eggplant)\b/i, { bg: '#dcd0e5', text: '#3f2658' }],
  [/\b(brown|cocoa|mocha)\b/i, { bg: '#ddc9b5', text: '#5a3a1f' }],
  [/\b(gray|grey|silver|heather)\b/i, { bg: '#dcdfe3', text: '#3e454d' }],
];

/**
 * Resolve a color name to a soft tinted swatch. Pure (no I/O), case-
 * insensitive, regex-based. Unknown names fall back to Sierra Teal soft so
 * the UI never breaks on a new color — and so a brand-on color is the
 * silent default rather than a flat gray that would hide the gap.
 */
export function resolveColorSwatch(name: string | undefined | null): ColorSwatch {
  if (!name) {
    return FALLBACK_SWATCH;
  }
  for (const [regex, swatch] of SWATCH_RULES) {
    if (regex.test(name)) {
      return swatch;
    }
  }
  return FALLBACK_SWATCH;
}

/**
 * Pure resolver for a roll's color display. Driven by the per-period color
 * map populated by cost-analysis-page after fetching rolls. Never throws;
 * always returns a renderable state so a 5xx on the lookup endpoint just
 * degrades the cell to a diagnostic without blocking the rest of the row.
 */
export function resolveColorDisplay(
  colorReference: string | null | undefined,
  colorMap: ReadonlyMap<string, ColorRecord> | undefined,
): ColorDisplay {
  if (!colorReference) {
    return { kind: 'uncolored' };
  }
  const record = colorMap?.get(colorReference);
  if (record) {
    return {
      kind: 'colored',
      name: record.name,
      labDipNumber: record.labDipNumber,
      swatch: resolveColorSwatch(record.name),
    };
  }
  return { kind: 'orphan', diagnostic: colorReference.slice(0, 8) };
}
